import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { FileUidInput } from "../fileUpload";
import { FormSection } from "./FormSection";
import { isSameMediaForAllLangs, mediaRequired, mediaSources } from "./normalizeMedia";

export type MediaBaseSource = "Image" | "Video" | "Filename" | "Background" | "BackgroundMusic";

type FileInputProps = {
  baseSource: MediaBaseSource;
  label: string;
  kind?: "image" | "audio" | "video" | "media" | "any";
  accept?: string;
  helperText?: string;
  required?: boolean;
};

export type LangCode = "AZ" | "RU" | "EN";

export const sameMediaFieldName = (base: MediaBaseSource) => `_same${base}`;

const pickFirstValue = (values: Record<string, unknown>, base: string) => {
  for (const src of mediaSources(base)) {
    const v = values[src];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

/** Вкладка «Общее»: галочка + один upload, если файлы одинаковые. */
export const LocalizedFileControls = ({
  baseSource,
  label,
  kind = "image",
  accept,
  helperText,
  required,
}: FileInputProps) => {
  const { getValues, setValue, watch } = useFormContext();
  const sameField = sameMediaFieldName(baseSource);
  const sharedSource = `${baseSource}RU`;
  const sameForAll = watch(sameField) !== false;
  const sharedValue = watch(sharedSource);

  useEffect(() => {
    if (getValues(sameField) === undefined) {
      setValue(sameField, isSameMediaForAllLangs(getValues(), baseSource), {
        shouldDirty: false,
      });
    }
  }, [baseSource, getValues, setValue, sameField]);

  useEffect(() => {
    if (!sameForAll || !sharedValue) return;
    setValue(`${baseSource}AZ`, sharedValue, { shouldDirty: true });
    setValue(`${baseSource}EN`, sharedValue, { shouldDirty: true });
    setValue(baseSource, sharedValue, { shouldDirty: true });
  }, [sameForAll, sharedValue, baseSource, setValue]);

  // У старых записей файл может лежать только в базовой колонке —
  // подставляем его в общее поле, чтобы не было «No file» при наличии файла
  // и чтобы required-валидация не падала на скрытом поле.
  useEffect(() => {
    if (!sameForAll || sharedValue) return;
    const current = pickFirstValue(getValues(), baseSource);
    if (current) {
      setValue(sharedSource, current, { shouldDirty: false });
    }
  }, [sameForAll, sharedValue, baseSource, getValues, setValue, sharedSource]);

  // Удаление файла в режиме «одинаковый» — чистим все языковые копии,
  // иначе эффект выше тут же подставит старый uid обратно.
  const handleSharedValueChange = (value: string) => {
    if (value || !sameForAll) return;
    for (const src of mediaSources(baseSource)) {
      if (src !== sharedSource) {
        setValue(src, "", { shouldDirty: true });
      }
    }
  };

  const handleSameToggle = (_: unknown, checked: boolean) => {
    setValue(sameField, checked, { shouldDirty: true });
    if (checked) {
      const current =
        (typeof sharedValue === "string" && sharedValue.trim()) ||
        pickFirstValue(getValues(), baseSource);
      if (current) {
        for (const src of mediaSources(baseSource)) {
          setValue(src, current, { shouldDirty: true });
        }
      }
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={sameForAll}
              onChange={handleSameToggle}
            />
          }
          label="Одинаковый для всех языков"
          sx={{ mr: 0 }}
        />
      </Stack>

      {sameForAll ? (
        <FileUidInput
          source={sharedSource}
          label={label}
          kind={kind}
          accept={accept}
          helperText={helperText ?? "Файл будет использован для AZ, RU и EN"}
          validate={required ? mediaRequired(baseSource) : undefined}
          onValueChange={handleSharedValueChange}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Загрузите файл отдельно на вкладках 🇦🇿 AZ, 🇷🇺 RU и 🇬🇧 EN
        </Typography>
      )}
    </Box>
  );
};

type LangFieldProps = FileInputProps & {
  lang: LangCode;
};

/** Языковая вкладка: upload только если галочка «одинаковый» снята. */
export const LocalizedFileLangField = ({
  baseSource,
  lang,
  label,
  kind = "image",
  accept,
  helperText,
  required,
}: LangFieldProps) => {
  const sameField = sameMediaFieldName(baseSource);
  const sameForAll = useWatch({ name: sameField });

  if (sameForAll !== false) {
    return null;
  }

  return (
    <FormSection title={label}>
      <FileUidInput
        source={`${baseSource}${lang}`}
        label={label}
        kind={kind}
        accept={accept}
        helperText={helperText}
        validate={required ? mediaRequired(baseSource) : undefined}
      />
    </FormSection>
  );
};

/** @deprecated Используйте LocalizedFileControls + LocalizedFileLangField */
export const LocalizedFileInput = LocalizedFileControls;
